import { useState, useEffect, useCallback } from 'react'
import { fetchContentfulEntries } from '../lib/contentful'

export interface Testimonial {
  id: string
  name: string
  company: string
  quote: string
  rating: number
  verified: boolean
}

// Contentful field shape for "testimonials" content type
interface ContentfulTestimonialFields {
  name: string
  titlerole?: string
  testimonial: string
  rating: number
}

const STORAGE_KEY = 'belo_testimonials'
const TESTIMONIALS_UPDATED_EVENT = 'belo_testimonials_updated'

function getStoredTestimonials(): Testimonial[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    return JSON.parse(raw)
  } catch {
    return []
  }
}

function saveTestimonials(testimonials: Testimonial[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(testimonials))
}

function dispatchTestimonialsUpdate() {
  window.dispatchEvent(new Event(TESTIMONIALS_UPDATED_EVENT))
}

export function addTestimonial(
  data: Omit<Testimonial, 'id' | 'verified'>,
): Promise<void> {
  return new Promise((resolve) => {
    const testimonials = getStoredTestimonials()
    const newTestimonial: Testimonial = {
      ...data,
      id: `test_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      verified: false,
    }
    testimonials.push(newTestimonial)
    saveTestimonials(testimonials)
    dispatchTestimonialsUpdate()
    resolve()
  })
}

export function removeTestimonial(id: string): void {
  const testimonials = getStoredTestimonials().filter((t) => t.id !== id)
  saveTestimonials(testimonials)
  dispatchTestimonialsUpdate()
}

export function toggleVerifyTestimonial(id: string): void {
  const testimonials = getStoredTestimonials()
  const index = testimonials.findIndex((t) => t.id === id)
  if (index !== -1) {
    testimonials[index].verified = !testimonials[index].verified
    saveTestimonials(testimonials)
    dispatchTestimonialsUpdate()
  }
}

function mapContentfulTestimonial(entry: {
  sys: { id: string }
  fields: ContentfulTestimonialFields
}): Testimonial {
  return {
    id: entry.sys.id,
    name: entry.fields.name || '',
    company: entry.fields.titlerole || '',
    quote: entry.fields.testimonial || '',
    rating: entry.fields.rating || 5,
    verified: true, // CMS entries are considered verified
  }
}

export function useTestimonials() {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const fetchFromCMS = useCallback(async () => {
    setLoading(true)
    setError(false)
    try {
      const entries =
        await fetchContentfulEntries<ContentfulTestimonialFields>(
          'testimonials',
        )
      const mapped = entries.map(mapContentfulTestimonial)
      setTestimonials(mapped)
      saveTestimonials(mapped)
    } catch (err) {
      console.error(
        'Failed to fetch testimonials from CMS, falling back to local:',
        err,
      )
      setError(true)
      setTestimonials(getStoredTestimonials())
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchFromCMS()
  }, [fetchFromCMS])

  useEffect(() => {
    const handler = () => setTestimonials(getStoredTestimonials())
    window.addEventListener(TESTIMONIALS_UPDATED_EVENT, handler)
    return () => window.removeEventListener(TESTIMONIALS_UPDATED_EVENT, handler)
  }, [])

  return {
    testimonials,
    loading,
    error,
    refetch: fetchFromCMS,
    toggleVerify: toggleVerifyTestimonial,
  }
}
